import React, {useContext, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {toast} from 'react-toastify';
import {Button, Input, Label} from 'reactstrap';
import {editStorefrontSettings, getStorefrontSettingsInfo} from '../../api/api';
import AuthContext from '../../context/AuthContext';
import handleUnauthorized401MutateError from '../../utils/handleUnauthorized401MutateError';
import Loader from '../../components/Loader';

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function defaultBusinessHours() {
    return DAY_NAMES.map((_, i) => ({
        day_of_week: i,
        is_closed: false,
        open_time: '09:00',
        close_time: '21:00',
    }));
}

function StorefrontSettingsTab() {
    const {refreshToken} = useContext(AuthContext);
    const queryClient = useQueryClient();
    const [settings, setSettings] = useState({});
    const [settingId, setSettingId] = useState(null);
    const [businessHours, setBusinessHours] = useState(defaultBusinessHours());

    const {refetch} = useQuery({
        queryKey: ['storefront-settings'],
        queryFn: () => getStorefrontSettingsInfo(),
        keepPreviousData: true,
        retry: false,
        onSuccess: (data) => {
            const storefrontSettings = data.results[0];
            setSettingId(storefrontSettings.id);
            setSettings(storefrontSettings);
            setBusinessHours(
                storefrontSettings.business_hours && storefrontSettings.business_hours.length > 0
                    ? storefrontSettings.business_hours
                    : defaultBusinessHours()
            );
        },
        onError: (err) => {
            handleUnauthorized401MutateError(err, refreshToken, refetch);
            if (err.response.status !== 401) {
                toast.error(`An error occurred! ${err?.response?.data?.detail}`);
            }
        }
    });

    const storefrontSettingMutation = useMutation(editStorefrontSettings, {
        onSuccess: () => {
            toast.success('Settings updated successfully!');
            queryClient.invalidateQueries(['storefront-settings']);
        },
        onError: (err) => {
            const data = err?.response?.data;
            if (data && typeof data === 'object' && !data.detail) {
                const firstError = Object.values(data)[0];
                toast.error(Array.isArray(firstError) ? firstError[0] : firstError);
            } else {
                toast.error(`An error occurred! ${data?.detail || 'Unknown error'}`);
            }
        },
    });

    const handleSettingsChange = (e) => {
        const {name, value} = e.target;
        setSettings((prevSettings) => ({
            ...prevSettings,
            [name]: value,
        }));
    };

    const handleBusinessHourChange = (dayOfWeek, field, value) => {
        setBusinessHours((prev) =>
            prev.map((day) =>
                day.day_of_week === dayOfWeek ? {...day, [field]: value} : day
            )
        );
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const formData = {...settings, business_hours: businessHours};
        storefrontSettingMutation.mutate({formData, settingId});
    };

    const businessHoursMode = settings.business_hours_mode || '24x7';

    return (
        <form onSubmit={handleSubmit}>
            <div className="row">
                <div className="settings-card ml-3">
                    <div className="card-box">
                        <h6 className="text-muted mb-3" style={{textTransform: 'uppercase', fontSize: 11, letterSpacing: 1}}>Storefront</h6>
                        <div className="form-group">
                            <Label>Enable Product Page (Storefront)</Label>
                            <Input
                                type="select"
                                name="is_storefront_enabled"
                                className="form-control"
                                value={settings.is_storefront_enabled ?? true}
                                onChange={handleSettingsChange}
                            >
                                <option value="true">YES</option>
                                <option value="false">NO</option>
                            </Input>
                            <small className="text-muted">When disabled, customers will not be able to view your product page or place online orders.</small>
                        </div>

                        <hr style={{borderColor: '#333', margin: '24px 0'}} />

                        <h6 className="text-muted mb-3" style={{textTransform: 'uppercase', fontSize: 11, letterSpacing: 1}}>Business Hours <small>(Online Orders)</small></h6>
                        <div className="form-group">
                            <Label>Business Hours Mode</Label>
                            <Input
                                type="select"
                                name="business_hours_mode"
                                className="form-control"
                                value={businessHoursMode}
                                onChange={handleSettingsChange}
                            >
                                <option value="24x7">Accept Orders 24x7</option>
                                <option value="SAME_EVERY_DAY">Same Hours Every Day</option>
                                <option value="CUSTOM_PER_DAY">Custom Hours Per Day</option>
                            </Input>
                        </div>

                        {businessHoursMode === 'SAME_EVERY_DAY' && (
                            <div className="d-flex" style={{gap: 16}}>
                                <div className="form-group" style={{flex: 1}}>
                                    <Label>Open Time</Label>
                                    <Input
                                        type="time"
                                        name="daily_open_time"
                                        className="form-control"
                                        value={settings.daily_open_time || ''}
                                        onChange={handleSettingsChange}
                                    />
                                </div>
                                <div className="form-group" style={{flex: 1}}>
                                    <Label>Close Time</Label>
                                    <Input
                                        type="time"
                                        name="daily_close_time"
                                        className="form-control"
                                        value={settings.daily_close_time || ''}
                                        onChange={handleSettingsChange}
                                    />
                                </div>
                            </div>
                        )}

                        {businessHoursMode === 'CUSTOM_PER_DAY' && (
                            <div style={{border: '1px solid #333', borderRadius: 6, padding: 16}}>
                                {businessHours
                                    .sort((a, b) => a.day_of_week - b.day_of_week)
                                    .map((day) => (
                                        <div key={day.day_of_week} className="d-flex align-items-center mb-2" style={{gap: 12}}>
                                            <div style={{width: 100, color: '#ccc', fontSize: 13, fontWeight: 500}}>
                                                {DAY_NAMES[day.day_of_week]}
                                            </div>
                                            <div style={{width: 80}}>
                                                <label className="d-flex align-items-center" style={{gap: 6, cursor: 'pointer', fontSize: 12, color: day.is_closed ? '#f5325c' : '#6e768e'}}>
                                                    <input
                                                        type="checkbox"
                                                        checked={day.is_closed}
                                                        onChange={(e) => handleBusinessHourChange(day.day_of_week, 'is_closed', e.target.checked)}
                                                    />
                                                    Closed
                                                </label>
                                            </div>
                                            {!day.is_closed && (
                                                <>
                                                    <Input
                                                        type="time"
                                                        value={day.open_time || ''}
                                                        onChange={(e) => handleBusinessHourChange(day.day_of_week, 'open_time', e.target.value)}
                                                        className="form-control"
                                                        style={{flex: 1}}
                                                    />
                                                    <span style={{color: '#6e768e'}}>to</span>
                                                    <Input
                                                        type="time"
                                                        value={day.close_time || ''}
                                                        onChange={(e) => handleBusinessHourChange(day.day_of_week, 'close_time', e.target.value)}
                                                        className="form-control"
                                                        style={{flex: 1}}
                                                    />
                                                </>
                                            )}
                                            {day.is_closed && (
                                                <span style={{color: '#f5325c', fontSize: 12, fontStyle: 'italic'}}>Not accepting orders</span>
                                            )}
                                        </div>
                                    ))}
                            </div>
                        )}

                        <hr style={{borderColor: '#333', margin: '24px 0'}} />

                        <h6 className="text-muted mb-3" style={{textTransform: 'uppercase', fontSize: 11, letterSpacing: 1}}>Customer Checkout</h6>
                        <div className="form-group">
                            <Label>Allow Customers to Select GST Treatment</Label>
                            <Input
                                type="select"
                                name="allow_customer_gst_treatment"
                                className="form-control"
                                value={settings.allow_customer_gst_treatment ?? true}
                                onChange={handleSettingsChange}
                            >
                                <option value="true">YES</option>
                                <option value="false">NO</option>
                            </Input>
                            <small className="text-muted">When set to NO, customers are locked to Regular B2B treatment regardless of their GSTIN.</small>
                        </div>

                        <Button type="submit" className="btn bx-btn-primary waves-effect waves-light" disabled={storefrontSettingMutation.isLoading || !settingId}>
                            {storefrontSettingMutation.isLoading ? <><Loader size={'small'}/>Saving...</> : 'Save'}
                        </Button>
                    </div>
                </div>
            </div>
        </form>
    );
}

export default StorefrontSettingsTab;
